/* Atelier — « champ de lumière » du haut de page.
   Shader plein écran (WebGL 1, sans librairie) : domain warping sur un bruit fbm, teinté avec les couleurs du thème,
   une source de lumière qui suit la souris, grain anti-banding. Rendu en demi-résolution, en pause hors écran. */
(() => {
  'use strict';
  const VS = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  const FS = `precision highp float;
uniform vec2 uRes;uniform float uTime;uniform vec2 uMouse;uniform vec3 uInk,uAccent,uGlow;uniform float uIntensity;
float h(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;mat2 r=mat2(.8,-.6,.6,.8);for(int i=0;i<5;i++){v+=a*n(p);p=r*p*2.02;a*=.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/uRes;vec2 p=(gl_FragCoord.xy-.5*uRes)/uRes.y;
  float t=uTime*.035;
  vec2 q=vec2(fbm(p*.9+t),fbm(p*.9+vec2(5.2,1.3)-t*.8));
  vec2 r=vec2(fbm(p*.9+1.6*q+vec2(1.7,9.2)+t*.6),fbm(p*.9+1.6*q+vec2(8.3,2.8)-t*.5));
  float f=fbm(p*.9+1.4*r);
  f=clamp((f-.22)/.6,0.,1.);
  vec2 m=(uMouse-.5)*vec2(uRes.x/uRes.y,1.);
  float light=exp(-dot(p-m,p-m)*1.6);
  vec3 col=uInk;
  col=mix(col,uAccent*.85,smoothstep(.18,.9,f)*.8*uIntensity);
  col=mix(col,uGlow,smoothstep(.5,1.,f*(.7+light*1.1))*.8*uIntensity);
  col+=uGlow*light*.22*uIntensity;
  col*=1.-.55*smoothstep(.4,1.15,length((uv-.5)*vec2(1.25,1.)));
  col+=(h(gl_FragCoord.xy+fract(uTime))-.5)*.028;
  gl_FragColor=vec4(col,1.);
}`;
  const FS_HEX = `precision highp float;
uniform vec2 uRes;uniform float uTime;uniform vec2 uMouse;uniform vec3 uInk,uAccent,uGlow;uniform float uIntensity;uniform float uOn;
float h1(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
vec4 hexc(vec2 uv){vec2 r=vec2(1.,1.7320508);vec2 hh=r*.5;vec2 a=mod(uv,r)-hh;vec2 b=mod(uv-hh,r)-hh;vec2 g=dot(a,a)<dot(b,b)?a:b;return vec4(g,uv-g);}
float hd(vec2 p){p=abs(p);return max(dot(p,vec2(.5,.8660254)),p.x);}
/* lumière d'un plafond en nid d'abeille vu depuis la position (w), largeur de pixel (fw) */
vec3 tubes(vec2 w,float fw,float soft,float t){
  vec4 c=hexc(w);vec2 g=c.xy;vec2 id=c.zw;
  float e=.5-hd(g);
  vec2 q=abs(g);float dv=min(length(q-vec2(.5,.288675)),length(q-vec2(0.,.57735)));
  float gap=smoothstep(.055,.075+fw,dv);
  float hw=.022;
  float core=1.-smoothstep(hw,hw+fw*1.5+soft,e);
  float halo=exp(-max(e-hw,0.)*(14./(1.+soft*30.)))*.22;
  float r=h1(id);
  float d0=length(id-vec2(0.,6.))*.07+r*.55;
  float on=step(d0,uOn);
  float fl=step(.45,h1(id+floor(t*26.)));
  float live=on*mix(fl,1.,smoothstep(d0+.05,d0+.42,uOn));
  float hub=1.-smoothstep(.05,.05+fw*2.,dv);
  vec3 tube=uGlow*(core*gap*1.25+halo*gap)*live*(.88+.12*r);
  return tube-vec3(.02)*hub;
}
void main(){
  vec2 p=(gl_FragCoord.xy-.5*uRes)/uRes.y;
  vec2 m=uMouse-.5;
  float hz=-.06;
  float yy=p.y-hz;
  float t=uTime;
  vec3 col=uInk*.9;
  float px=1./uRes.y;
  float drift=t*.05;
  float S=2.9;
  if(yy>0.){
    float d=1./yy;                     /* distance au plafond */
    vec2 w=vec2((p.x+m.x*.18)*d,d+drift)*S;
    float fw=px*d*d*S*1.2;
    vec3 L=tubes(w,fw,0.,t);
    float fog=exp(-d*.13)*mix(.55,1.,smoothstep(1.6,4.,d));
    col+=L*fog*uIntensity;
    col+=uGlow*.035*fog*min(uOn,1.);           /* plafond éclairé */
  } else {
    float d=2.4/(-yy);                 /* reflet dans un sol laqué */
    float n=h1(floor(gl_FragCoord.xy*.5))*.004;
    vec2 w=vec2((p.x+m.x*.18)*d,d+drift)*S+n;
    float fw=px*d*d*S*.5;
    vec3 L=tubes(w,fw,.03+(-yy)*.08,t);
    float fog=exp(-d*.09);
    col+=L*fog*.32*uIntensity;
    col+=uGlow*.02*fog*min(uOn,1.);
  }
  float hzg=exp(-abs(yy)*38.)*.12*min(uOn,1.);   /* ligne d'horizon */
  col+=uGlow*hzg;
  float lm=exp(-dot(p-vec2(m.x*1.6,m.y),p-vec2(m.x*1.6,m.y))*3.);
  col+=uAccent*lm*.05*uIntensity;
  vec2 uv=gl_FragCoord.xy/uRes;
  col*=1.-.5*smoothstep(.45,1.2,length((uv-.5)*vec2(1.1,1.)));
  col+=(h1(gl_FragCoord.xy+fract(t))-.5)*.025;
  gl_FragColor=vec4(col,1.);
}`;
  const hex = s => { s = (s || '#000').replace('#', ''); if (s.length === 3) s = s.split('').map(c => c + c).join(''); const v = parseInt(s, 16); return [(v >> 16 & 255) / 255, (v >> 8 & 255) / 255, (v & 255) / 255].map(c => Math.pow(c, 1)); };

  function mount(host) {
    if (host.dataset.on) return; host.dataset.on = '1';
    const cv = document.createElement('canvas'); cv.setAttribute('aria-hidden', 'true');
    const gl = cv.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false, powerPreference: 'low-power' });
    if (!gl) return;
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) { console.warn(gl.getShaderInfoLog(o)); return null; } return o; };
    const pr = gl.createProgram(); const style = host.dataset.style || 'field'; const v = sh(gl.VERTEX_SHADER, VS), f = sh(gl.FRAGMENT_SHADER, style === 'honeycomb' ? FS_HEX : FS); if (!v || !f) return;
    gl.attachShader(pr, v); gl.attachShader(pr, f); gl.linkProgram(pr); gl.useProgram(pr);
    const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = n => gl.getUniformLocation(pr, n);
    const cs = getComputedStyle(document.documentElement);
    const col = (name, fb) => hex((host.dataset[name] || cs.getPropertyValue('--' + name) || fb).trim());
    gl.uniform3fv(U('uInk'), col('ink', '#0e0e0c')); gl.uniform3fv(U('uAccent'), col('accent', '#ff6a2b')); gl.uniform3fv(U('uGlow'), col('glow', '#ffc27a'));
    gl.uniform1f(U('uIntensity'), parseFloat(host.dataset.intensity || 1));
    host.appendChild(cv);
    const m = (window.theme && window.theme.motion) || {}; let pref = null; try { pref = localStorage.getItem('motion'); } catch (e) {}
    const reduce = pref === 'off' || (pref !== 'on' && m.respectReduce === true && matchMedia('(prefers-reduced-motion: reduce)').matches);
    let W = 0, H = 0, raf = 0, visible = true, t0 = performance.now(), mx = 0.62, my = 0.45, tx = mx, ty = my;
    const size = () => { const r = host.getBoundingClientRect(); const s = Math.min(window.devicePixelRatio || 1, 2) * (innerWidth < 760 ? 0.4 : 0.5); W = Math.max(2, Math.round(r.width * s)); H = Math.max(2, Math.round(r.height * s)); cv.width = W; cv.height = H; gl.viewport(0, 0, W, H); gl.uniform2f(U('uRes'), W, H); };
    const draw = now => { mx += (tx - mx) * 0.05; my += (ty - my) * 0.05; const el = (now - t0) / 1000; gl.uniform1f(U('uOn'), reduce ? 9 : Math.max(0, el - 0.35) * 1.15); gl.uniform1f(U('uTime'), el + 20); gl.uniform2f(U('uMouse'), mx, my); gl.drawArrays(gl.TRIANGLES, 0, 3); };
    const loop = now => { raf = 0; draw(now); if (visible && !document.hidden) raf = requestAnimationFrame(loop); };
    size(); draw(performance.now()); host.classList.add('is-live');
    if (reduce) { addEventListener('resize', () => { size(); draw(performance.now()); }); return; }
    raf = requestAnimationFrame(loop);
    addEventListener('resize', size, { passive: true });
    addEventListener('pointermove', e => { const r = host.getBoundingClientRect(); tx = (e.clientX - r.left) / r.width; ty = 1 - (e.clientY - r.top) / r.height; }, { passive: true });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible && !raf) raf = requestAnimationFrame(loop); }).observe(host);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && visible && !raf) raf = requestAnimationFrame(loop); });
  }
  const boot = () => document.querySelectorAll('[data-hero-field]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
